import { useCallback, useMemo } from "react";

import { ListEmptyIcon, POIIcon } from "@/assets/icons";
import { MaskIcon } from "@/components/ui/MaskIcon";
import { TreeSelection } from "@/components/ui/TreeSelection";
import { cn } from "@/lib/utils";
import {
  PoiBrandGroupedByCategory,
  PoiBrandSearch,
  PoiCategory,
  PoiGroup,
  PoiIndustry
} from "@/types/poiParameter";

import { PoiGroupList } from "./PoiGroupList";

export interface PoiBrandTreeSelectorProps {
  activeGroup: string;
  setActiveGroup: (groupId: string) => void;
  expandedCategories: Set<string>;
  toggleCategoryExpand: (compositeKey: string) => void;
  selectedBrands: PoiBrandGroupedByCategory;
  handleSelectCategory: (
    categoryCode: string,
    categoryName: string,
    isChecked: boolean
  ) => Promise<void>;
  handleSelectBrand: (
    categoryCode: string,
    categoryName: string,
    brand: PoiBrandSearch,
    isChecked: boolean
  ) => void;
  currentIndustry?: PoiIndustry;
  currentGroup?: PoiGroup;
  isLoading?: { [nodeId: string]: boolean };
}

export const PoiBrandTreeSelector = ({
  activeGroup,
  setActiveGroup,
  expandedCategories,
  toggleCategoryExpand,
  selectedBrands,
  handleSelectCategory,
  handleSelectBrand,
  currentIndustry,
  currentGroup,
  isLoading
}: PoiBrandTreeSelectorProps) => {
  // Transform selectedBrands to the format expected by TreeSelection
  const selectedItemsMap = useMemo(() => {
    const map: Record<string, PoiBrandSearch[]> = {};
    for (const [key, selection] of Object.entries(selectedBrands)) {
      map[key] = selection.brands;
    }
    return map;
  }, [selectedBrands]);

  // Handle select all for TreeSelection
  const onTreeSelectAll = useCallback(
    (category: PoiCategory, checked: boolean) => {
      if (!currentGroup) return;
      handleSelectCategory(category.code, category.name, checked);
    },
    [currentGroup, handleSelectCategory]
  );

  // Handle select item for TreeSelection
  const onTreeSelectItem = useCallback(
    (category: PoiCategory, brand: PoiBrandSearch, checked: boolean) => {
      if (!currentGroup) return;
      handleSelectBrand(category.code, category.name, brand, checked);
    },
    [currentGroup, handleSelectBrand]
  );

  return (
    <div className="flex flex-col w-full h-full bg-white border border-gray-200 rounded-xl pt-4 px-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-200 pb-4 shrink-0">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-gray-900 font-plus-jakarta">
            {currentIndustry?.name}
          </h3>
          <p className="text-xs text-gray-500 font-plus-jakarta">
            Select available type of POI
          </p>
        </div>
        <div className="w-[42px] h-[42px] rounded-[10.5px] border border-gray-200 flex items-center justify-center bg-white shrink-0">
          {currentGroup?.icon_url ? (
            <MaskIcon
              url={currentGroup.icon_url}
              className="w-6 h-6 text-dim-gray"
            />
          ) : (
            <POIIcon className="w-6 h-6 fill-dim-gray" />
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Group List (Column 1) */}
        <PoiGroupList
          currentIndustry={currentIndustry}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
        />

        {/* Category Tree (Column 2) */}
        <div className="flex-1 flex flex-col pb-4 overflow-hidden">
          <TreeSelection<PoiCategory, PoiBrandSearch>
            data={
              currentGroup?.poi_categories?.sort((a, b) =>
                a.name.localeCompare(b.name)
              ) || []
            }
            expandedNodes={expandedCategories}
            onToggleExpand={toggleCategoryExpand}
            selectedItems={selectedItemsMap}
            onSelectAll={onTreeSelectAll}
            onSelectItem={onTreeSelectItem}
            title="Category"
            className={cn(
              "h-full",
              !currentGroup?.poi_categories?.length && "overflow-y-visible"
            )}
            getParentKey={(category) => category.code}
            getChildKey={(brand) => brand.brand_code}
            getParentLabel={(category) => category.name}
            getChildLabel={(brand) => brand.brand_name}
            getChildren={(category) => category.poi_brands || []}
            hasChildren={(node, parent) => {
              if (!parent && "poi_brands" in node) {
                return (node.poi_brands || []).length > 0;
              }
              return !parent;
            }}
            isLoading={isLoading}
            emptyPlaceholder={
              <div className="text-sm text-center flex flex-col items-start justify-center gap-1 px-2 py-4 h-full w-full">
                <div className="w-full flex items-center justify-center">
                  <ListEmptyIcon className="w-6 h-6 fill-light-gray" />
                </div>
                <p className="font-semibold text-txtBody w-full">
                  Select a group to see categories
                </p>
                <p className="text-xs text-txtPlaceholder w-full">
                  Categories will appear here after a group is available.
                </p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};
